import { useEffect, useRef, useState } from "react";
import { AssetRecordType, createShapeId, type TLImageShape, type TLVideoShape } from "@tldraw/tlschema";
import { api } from "./api";
import { creativeSourceKey, type CreativeDraft } from "./creative-tools";
import type { WorkspaceModel } from "./useWorkspace";
import { sheetFor } from "./shapes";
import { VIDEO_GENERATION_ENABLED } from "../shared/features";
import {
  VIDEO_MODEL,
  type VideoJob,
  type VideoPrice,
} from "../shared/video-generation";

type Price = VideoPrice & {
  quoteId: string;
  expires: number;
  fingerprint: string;
};
type Status = {
  configured: boolean;
  canGenerate: boolean;
  dailyLimit: number;
  jobs: (VideoJob & { canCheck?: boolean })[];
};
export function useVideoGeneration(model: WorkspaceModel, enabled: boolean) {
  const [status, setStatus] = useState<Status>({
    configured: false,
    canGenerate: false,
    dailyLimit: 3,
    jobs: [],
  });
  const [price, setPrice] = useState<Price | null>(null),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  const pending = useRef(false),
    live = useRef(true);
  const route = `/api/rooms/${model.room.id}/creative-videos`;
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);
  useEffect(() => {
    if (!VIDEO_GENERATION_ENABLED || !enabled || model.room.platform === "cloudflare") return;
    let active = true;
    const refresh = () =>
      void api<Status>(route)
        .then((v) => {
          if (active) setStatus(v);
        })
        .catch(() => {
          if (active)
            setError(
              "Could not check video status. Reopen Animate to reconnect.",
            );
        });
    refresh();
    const timer = setInterval(refresh, 4000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [enabled, route, model.room.platform]);
  const payload = (shape: TLImageShape, draft: CreativeDraft) => {
    const end = draft.endImage
      ? model.editor.getShape<TLImageShape>(
          draft.endImage as TLImageShape["id"],
        )
      : undefined;
    if (draft.endImage && end?.type !== "image")
      throw new Error("Choose the end image again.");
    return {
      source: { id: shape.id, key: creativeSourceKey(shape) },
      prompt: draft.videoPrompt,
      duration: draft.duration,
      resolution: draft.resolution,
      audio: draft.audio,
      ...(end ? { endImage: { id: end.id, key: creativeSourceKey(end) } } : {}),
    };
  };
  const fingerprint = (shape: TLImageShape, draft: CreativeDraft) => {
    try {
      return JSON.stringify(payload(shape, draft));
    } catch {
      return "";
    }
  };
  const run = async (action: () => Promise<void>) => {
    if (!VIDEO_GENERATION_ENABLED) return;
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    setError("");
    try {
      await action();
    } catch (e) {
      if (live.current) setError((e as Error).message);
    } finally {
      pending.current = false;
      if (live.current) setBusy(false);
    }
  };
  const estimate = (shape: TLImageShape, draft: CreativeDraft) =>
    run(async () => {
      const body = payload(shape, draft),
        result = await api<Omit<Price, "fingerprint">>(
          `${route}/estimate`,
          body,
        );
      if (live.current)
        setPrice({ ...result, fingerprint: JSON.stringify(body) });
    });
  const submit = (shape: TLImageShape, draft: CreativeDraft) =>
    run(async () => {
      if (
        !model.canEdit ||
        model.editor.getIsReadonly() ||
        model.editor.isShapeOrAncestorLocked(shape)
      )
        throw new Error("Select an editable, unlocked image.");
      const body = payload(shape, draft);
      if (
        !price ||
        price.expires < Date.now() ||
        price.fingerprint !== JSON.stringify(body)
      )
        throw new Error("Review pricing again before generating.");
      const job = await api<VideoJob>(route, {
        ...body,
        id: crypto.randomUUID(),
        quoteId: price.quoteId,
      });
      if (live.current) {
        setPrice(null);
        setStatus((s) => ({
          ...s,
          jobs: [job, ...s.jobs.filter((j) => j.id !== job.id)],
        }));
      }
    });
  const check = (job: VideoJob) =>
    run(async () => {
      await api(`${route}/${job.id}/check`, {});
    });
  const add = (job: VideoJob) => {
    const { editor } = model;
    if (!job.output || !model.canEdit || editor.getIsReadonly()) return;
    const source = editor.getShape(job.sourceId as TLImageShape["id"]);
    if (!source || editor.isShapeOrAncestorLocked(source)) {
      setError(
        "The source is missing or locked. Unlock it before placing the video.",
      );
      return;
    }
    const id = createShapeId(`video-${job.id}`);
    if (editor.getShape(id)) {
      editor.select(id);
      return;
    }
    const bounds = editor.getShapePageBounds(source);
    if (!bounds) return;
    const w = Math.min(640, bounds.w),
      h = (w * job.output.h) / job.output.w;
    let x = bounds.maxX + 32;
    const y = bounds.minY;
    const boxes = editor
      .getCurrentPageShapes()
      .filter((s) => s.type !== "sheet")
      .map((s) => editor.getShapePageBounds(s))
      .filter(Boolean);
    for (let i = 0; i <= boxes.length; i++) {
      const hit = boxes.find(
        (b) =>
          b &&
          x < b.maxX + 16 &&
          x + w > b.minX - 16 &&
          y < b.maxY + 16 &&
          y + h > b.minY - 16,
      );
      if (!hit) break;
      x = hit.maxX + 32;
    }
    const assetId = AssetRecordType.createId(`video-${job.id}`);
    editor.markHistoryStoppingPoint("Add concept video");
    editor.run(() => {
      if (!editor.getAsset(assetId))
        editor.createAssets([
          AssetRecordType.create({
            id: assetId,
            type: "video",
            props: {
              src: job.output!.src,
              name: "Concept video.mp4",
              mimeType: "video/mp4",
              w: job.output!.w,
              h: job.output!.h,
              isAnimated: true,
            },
            meta: {},
          }),
        ]);
      editor.createShape<TLVideoShape>({
        id,
        type: "video",
        x,
        y,
        props: {
          assetId,
          w,
          h,
          autoplay: false,
          playing: false,
          altText: "Concept video",
        },
        meta: {
          name: "Concept video",
          aiModel: VIDEO_MODEL,
          aiSource: job.sourceId,
          aiJob: job.id,
          poster: job.output!.poster,
        },
      });
      const sheet = sheetFor(editor, source);
      if (sheet && !editor.isShapeOrAncestorLocked(sheet))
        editor.reparentShapes([id], sheet.id);
      editor.select(id);
    });
    editor.zoomToSelection({ animation: { duration: 200 } });
  };
  return {
    status,
    price,
    error,
    busy,
    fingerprint,
    estimate,
    submit,
    check,
    add,
  };
}
